import TrackArtistCredits from "./TrackArtistCredits";
import BpmInput from "../ui/BpmInput";
import SetTrackArtists from "./SetTrackArtists";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Alert from "@mui/material/Alert";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import MenuSurface from "../ui/MenuSurface";
import MenuItem from "@mui/material/MenuItem";
import useEditorForm from "../hooks/useEditorForm";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TextInput from "../ui/TextInput";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import React from "react";
import { CSS } from "@dnd-kit/utilities";
import useAsyncAction from "../hooks/useAsyncAction";
import { describeApiError } from "../api/request";
export default function SetTrackRow({
  setTrack,
  index,
  onRemove,
  onNavigate,
  onUpdate,
  attributes = {},
  listeners = {},
  setNodeRef,
  transform,
  transition,
  isDragging,
}) {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const action = useAsyncAction();
  const track = setTrack.track || setTrack;
  const title = track.title || track.name || "";
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [fields, setFields, editorForm] = useEditorForm({
    bpm: setTrack.bpm ?? track.bpm ?? "",
    duration: setTrack.duration ?? track.duration ?? "",
    notes: setTrack.notes || track.notes || "",
  });
  React.useEffect(() => {
    if (editing) return;
    setFields({
      bpm: setTrack.bpm ?? track.bpm ?? "",
      duration: setTrack.duration ?? track.duration ?? "",
      notes: setTrack.notes || track.notes || "",
    });
  }, [setTrack, track, editing, setFields]);
  const handleChange = (field, value) => {
    setFields((f) => ({
      ...f,
      [field]: value,
    }));
  };
  const handleSave = async () => {
    if (
      await action.run(() =>
        onUpdate(setTrack.id, {
          bpm: fields.bpm === "" ? null : fields.bpm,
          duration: fields.duration,
          notes: fields.notes,
        }),
      )
    )
      setEditing(false);
  };
  const handleCancel = () => {
    action.clearError();
    setFields({
      bpm: setTrack.bpm ?? track.bpm ?? "",
      duration: setTrack.duration ?? track.duration ?? "",
      notes: setTrack.notes || track.notes || "",
    });
    setEditing(false);
  };
  return (
    <TableRow
      className={`track-row${editing ? " editing" : ""}${isDragging ? " dragging" : ""}`}
      ref={setNodeRef}
      aria-busy={action.pending}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {editing ? (
        <TableCell>
          <IconButton
            type="button"
            className="cancel-edit"
            onClick={handleCancel}
            aria-label="Cancel edit"
            disabled={action.pending}
          >
            <CloseIcon />
          </IconButton>
        </TableCell>
      ) : (
        <TableCell>
          <IconButton
            className="drag-handle"
            aria-label="Drag to reorder"
            title="Drag to reorder"
            {...listeners}
            {...attributes}
          >
            <DragIndicatorIcon />
          </IconButton>
        </TableCell>
      )}
      <TableCell>{index + 1}</TableCell>
      <TableCell>
        <div>
          <Button
            type="button"
            className="track-title"
            variant="text"
            onClick={onNavigate}
          >
            {title}
          </Button>
          <SetTrackArtists track={track} />
          <TrackArtistCredits track={track} />
        </div>
      </TableCell>
      <TableCell>{track.position || "—"}</TableCell>
      {editing ? (
        <>
          <TableCell>
            <BpmInput
              className="bpm-input"
              type="number"
              value={fields.bpm}
              aria-label="BPM"
              disabled={action.pending}
              onChange={(e) => handleChange("bpm", e.target.value)}
            />
          </TableCell>
          <TableCell>
            <TextInput
              className="duration-input"
              type="text"
              value={fields.duration}
              aria-label="Duration"
              disabled={action.pending}
              onChange={(e) => handleChange("duration", e.target.value)}
            />
          </TableCell>
          <TableCell className="notes">
            <TextInput
              className="notes-input"
              value={fields.notes}
              aria-label="Notes"
              disabled={action.pending}
              onChange={(e) => handleChange("notes", e.target.value)}
              multiline
              maxRows={4}
            />
          </TableCell>
          <TableCell>
            <IconButton
              type="button"
              className="save-edit"
              onClick={editorForm.handleSubmit(handleSave)}
              aria-label="Save changes"
              disabled={action.pending}
            >
              <SaveIcon />
            </IconButton>
            {action.error && (
              <Alert role="alert" severity="error">
                {describeApiError(action.error)}
              </Alert>
            )}
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>{fields.bpm || track.default_bpm || "—"}</TableCell>
          <TableCell>{fields.duration || "—"}</TableCell>
          <TableCell className="notes">{fields.notes || "—"}</TableCell>
          <TableCell>
            <div className="menu row-menu">
              <IconButton
                type="button"
                className="menu-toggle"
                onClick={(event) => {
                  setMenuAnchor(event.currentTarget);
                  setMenuOpen((o) => !o);
                }}
                aria-label="Track options"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <MoreVertIcon />
              </IconButton>
              {menuOpen && (
                <MenuSurface
                  anchorEl={menuAnchor}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  fitBelow
                  onClose={() => setMenuOpen(false)}
                >
                  <MenuItem
                    onClick={() => {
                      setEditing(true);
                      setMenuOpen(false);
                    }}
                    component="button"
                  >
                    Edit Track
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    sx={{ color: "error.main" }}
                    onClick={() => {
                      onRemove && onRemove();
                      setMenuOpen(false);
                    }}
                    component="button"
                  >
                    Delete Track
                  </MenuItem>
                </MenuSurface>
              )}
            </div>
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
